import React, { useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Switch, TextField, Typography, Alert, Stack, Tooltip,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import { LocalizationProvider, TimePicker, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  useGetPlaylistGroupQuery, useUpdatePlaylistGroupItemMutation, useLazyPreviewScheduleQuery,
} from "../../../Api/attoDeskApi";
import {
  IPlaylistGroup, IPlaylistGroupItem, IScheduleConflict, MenuType,
} from "../../../Api/Interface/api.interface";
import { TabCard, NeedsGroup } from "./menuTabsUi";
import { useNotifier } from "../../../Core/Notifier";
import { BORDER, GOLD, NAVY, TEXT_SECONDARY, TEXT_PRIMARY } from "../../../theme/tokens";
import {
  WEEK_DAYS, to12h, to24h, parseTime, minutesOf, dayIsSet, toggleDay,
} from "./menuScheduleShared";
import { MenuTabProps } from "./MenuTabs";

const MENU_COLORS: Record<string, string> = {
  default_menu: "#64748b",
  daily_special: "#7c3aed",
  time_based_menu: "#0891b2",
  holiday_menu: "#dc2626",
  promotional_campaign: "#ea580c",
  pos_synced_menu: "#00838f",
  emergency_override: "#b91c1c",
  standard_playlist: "#3b82f6",
};
const colorFor = (t?: MenuType | string) => MENU_COLORS[t || "standard_playlist"] || "#3b82f6";

interface Draft {
  itemId: number;
  name: string;
  menuType?: MenuType;
  allDay: boolean;
  start: dayjs.Dayjs | null;
  end: dayjs.Dayjs | null;
  days: number; // bitmask
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
  priority: number;
  isActive: boolean;
}

const ScheduleTab: React.FC<MenuTabProps> = ({ groupId }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, isLoading, refetch } = useGetPlaylistGroupQuery(groupId ?? 0, { skip: !groupId });
  const [update, { isLoading: saving }] = useUpdatePlaylistGroupItemMutation();
  const [runPreview, { data: previewData, isFetching: previewing }] = useLazyPreviewScheduleQuery();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [conflicts, setConflicts] = useState<IScheduleConflict[] | null>(null);
  const [previewAt, setPreviewAt] = useState<dayjs.Dayjs | null>(dayjs());

  const group = data?.data as unknown as IPlaylistGroup | undefined;
  const items: IPlaylistGroupItem[] = useMemo(() => group?.items ?? [], [group]);

  if (!groupId) return <NeedsGroup />;

  const openEditor = (it: IPlaylistGroupItem) => {
    setConflicts(null);
    setDraft({
      itemId: it.id,
      name: it.playlist?.name ?? `Menu ${it.playlist_id}`,
      menuType: it.playlist?.menu_type,
      allDay: !it.start_time && !it.end_time,
      start: parseTime(it.start_time),
      end: parseTime(it.end_time),
      days: it.days_of_week ?? 0,
      startDate: it.start_date ? dayjs(it.start_date) : null,
      endDate: it.end_date ? dayjs(it.end_date) : null,
      priority: it.priority ?? 0,
      isActive: it.is_active,
    });
  };

  const saveDraft = async (force = false) => {
    if (!draft || !groupId) return;
    const body: any = {
      is_active: draft.isActive,
      priority: draft.priority,
      days_of_week: draft.days || null,
      start_time: draft.allDay ? null : to24h(draft.start),
      end_time: draft.allDay ? null : to24h(draft.end),
      start_date: draft.startDate ? draft.startDate.format("YYYY-MM-DD") : null,
      end_date: draft.endDate ? draft.endDate.format("YYYY-MM-DD") : null,
    };
    if (force) body.force = true;
    try {
      await update({ groupId, itemId: draft.itemId, body }).unwrap();
      showMessage("Schedule saved.");
      setDraft(null);
      setConflicts(null);
      refetch();
    } catch (e: any) {
      const c = e?.data?.conflicts as IScheduleConflict[] | undefined;
      if (c && c.length) { setConflicts(c); }
      else { showErrorMessage("Could not save the schedule."); }
    }
  };

  const doPreview = () => {
    runPreview({ groupId, at: previewAt ? previewAt.toISOString() : undefined });
  };
  const preview = previewData?.data as any;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2.5}>
        {/* Weekly grid */}
        <TabCard title="Weekly Schedule" subtitle="When each menu plays across the week"
          icon={<AccessTimeIcon fontSize="small" />}>
          {isLoading ? (
            <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={26} /></Box>
          ) : items.length === 0 ? (
            <Typography sx={{ color: TEXT_SECONDARY, textAlign: "center", py: 4 }}>
              No menus in this group yet. Add menus on the group page, then schedule them here.
            </Typography>
          ) : (
            <WeekGrid items={items} onEdit={openEditor} />
          )}
        </TabCard>

        {/* Menu schedule list */}
        <TabCard title="Menus in this schedule">
          <Stack spacing={1}>
            {items.map((it) => (
              <Box key={it.id} sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                border: `1px solid ${BORDER}`, borderRadius: 2, px: 1.5, py: 1, gap: 1,
                opacity: it.is_active ? 1 : 0.55,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colorFor(it.playlist?.menu_type), flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 700, color: NAVY }}>{it.playlist?.name}</Typography>
                    <Typography noWrap sx={{ fontSize: 11.5, color: TEXT_SECONDARY }}>
                      {scheduleSummary(it)}
                    </Typography>
                  </Box>
                </Box>
                <Button size="small" startIcon={<EditCalendarIcon />} onClick={() => openEditor(it)}
                  sx={{ textTransform: "none", fontWeight: 700, color: NAVY, flexShrink: 0 }}>
                  Edit schedule
                </Button>
              </Box>
            ))}
          </Stack>
        </TabCard>

        {/* Preview */}
        <TabCard title="Preview" subtitle="What will play at a chosen date & time">
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TimePicker label="Time" value={previewAt} onChange={(v) => setPreviewAt(v)}
              slotProps={{ textField: { size: "small" } }} />
            <DatePicker label="Date" value={previewAt} onChange={(v) => setPreviewAt(v)}
              slotProps={{ textField: { size: "small" } }} />
            <Button variant="outlined" onClick={doPreview} disabled={previewing}
              sx={{ textTransform: "none", fontWeight: 700 }}>
              {previewing ? "…" : "Preview"}
            </Button>
            {preview && (
              <Chip
                label={preview.override_active
                  ? "Override active"
                  : `Plays: ${(preview.playlists || []).map((p: any) => p.name).join(", ") || "—"} (${preview.rule})`}
                sx={{ fontWeight: 700, bgcolor: "#EEF2F6" }} />
            )}
          </Box>
        </TabCard>
      </Stack>

      {/* Schedule editor */}
      <Dialog open={!!draft} onClose={() => setDraft(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Schedule · {draft?.name}</DialogTitle>
        <DialogContent>
          {conflicts && conflicts.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This overlaps: {conflicts.map((c) => `${c.menu} (${c.days.join("/")} ${to12h(c.start_time)}–${to12h(c.end_time)})`).join("; ")}.
            </Alert>
          )}
          {draft && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} />
                <Typography sx={{ fontSize: 14 }}>Rule enabled</Typography>
              </Box>

              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.75 }}>DAYS</Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {WEEK_DAYS.map((label, idx) => {
                    const on = dayIsSet(draft.days, idx);
                    return (
                      <Chip key={label} label={label} size="small"
                        onClick={() => setDraft({ ...draft, days: toggleDay(draft.days || 0, idx) })}
                        sx={{
                          fontWeight: 700, cursor: "pointer",
                          bgcolor: on ? NAVY : "#EEF2F6", color: on ? "#fff" : TEXT_PRIMARY,
                        }} />
                    );
                  })}
                  <Tooltip title="Every day">
                    <Chip label="All" size="small" variant="outlined"
                      onClick={() => setDraft({ ...draft, days: 0 })}
                      sx={{ fontWeight: 700, cursor: "pointer" }} />
                  </Tooltip>
                </Box>
                {!draft.days && (
                  <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.5 }}>Plays every day.</Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch checked={draft.allDay} onChange={(e) => setDraft({ ...draft, allDay: e.target.checked })} />
                <Typography sx={{ fontSize: 14 }}>All day</Typography>
              </Box>

              {!draft.allDay && (
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <TimePicker label="Start" value={draft.start} onChange={(v) => setDraft({ ...draft, start: v })}
                    slotProps={{ textField: { size: "small", fullWidth: true } }} />
                  <TimePicker label="End" value={draft.end} onChange={(v) => setDraft({ ...draft, end: v })}
                    slotProps={{ textField: { size: "small", fullWidth: true } }} />
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 1.5 }}>
                <DatePicker label="Start date (optional)" value={draft.startDate}
                  onChange={(v) => setDraft({ ...draft, startDate: v })}
                  slotProps={{ textField: { size: "small", fullWidth: true } }} />
                <DatePicker label="End date (optional)" value={draft.endDate}
                  onChange={(v) => setDraft({ ...draft, endDate: v })}
                  slotProps={{ textField: { size: "small", fullWidth: true } }} />
              </Box>

              <TextField label="Priority" type="number" size="small" value={draft.priority}
                onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) })}
                helperText="Higher wins when two menus overlap." sx={{ maxWidth: 200 }} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          {conflicts && conflicts.length > 0 && (
            <Button color="warning" disabled={saving} onClick={() => saveDraft(true)}
              sx={{ textTransform: "none", fontWeight: 700 }}>Save anyway</Button>
          )}
          <Button variant="contained" disabled={saving} onClick={() => saveDraft(false)}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: NAVY, "&:hover": { bgcolor: "#08304a" } }}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

/* ---- weekly grid rendering ---- */

const START_HOUR = 0;
const HOURS = 24;

const WeekGrid: React.FC<{ items: IPlaylistGroupItem[]; onEdit: (it: IPlaylistGroupItem) => void }> = ({ items, onEdit }) => {
  // For each day (0=Mon..6=Sun) collect blocks.
  const blocksByDay: Array<Array<{ it: IPlaylistGroupItem; top: number; height: number; allDay: boolean }>> =
    Array.from({ length: 7 }, () => []);

  items.forEach((it) => {
    if (!it.is_active) return;
    const s = minutesOf(it.start_time);
    const e = minutesOf(it.end_time);
    const allDay = s === null && e === null;
    for (let d = 0; d < 7; d++) {
      if (!dayIsSet(it.days_of_week, d)) continue;
      if (allDay) { blocksByDay[d].push({ it, top: 0, height: 100, allDay: true }); continue; }
      const ss = s ?? 0, ee = e ?? 1440;
      if (ss <= ee) {
        blocksByDay[d].push({ it, top: (ss / 1440) * 100, height: ((ee - ss) / 1440) * 100, allDay: false });
      } else {
        // overnight: head today, tail next day
        blocksByDay[d].push({ it, top: (ss / 1440) * 100, height: ((1440 - ss) / 1440) * 100, allDay: false });
        const nd = (d + 1) % 7;
        blocksByDay[nd].push({ it, top: 0, height: (ee / 1440) * 100, allDay: false });
      }
    }
  });

  return (
    <Box sx={{ display: "flex", gap: 0.5, overflowX: "auto" }}>
      {/* hour axis */}
      <Box sx={{ width: 44, flexShrink: 0, position: "relative", height: 380, mt: 2.5 }}>
        {Array.from({ length: HOURS / 3 + 1 }, (_, i) => (
          <Typography key={i} sx={{
            position: "absolute", top: `${(i * 3 / HOURS) * 100}%`, right: 4,
            fontSize: 10, color: TEXT_SECONDARY, transform: "translateY(-50%)",
          }}>
            {to12h(`${START_HOUR + i * 3}:00`)}
          </Typography>
        ))}
      </Box>
      {WEEK_DAYS.map((label, d) => (
        <Box key={label} sx={{ flex: 1, minWidth: 88 }}>
          <Typography sx={{ textAlign: "center", fontWeight: 800, fontSize: 12, color: NAVY, mb: 0.5 }}>{label}</Typography>
          <Box sx={{ position: "relative", height: 380, border: `1px solid ${BORDER}`, borderRadius: 1, bgcolor: "#FAFBFC" }}>
            {Array.from({ length: HOURS / 3 }, (_, i) => (
              <Box key={i} sx={{ position: "absolute", top: `${((i + 1) * 3 / HOURS) * 100}%`, left: 0, right: 0, borderTop: `1px dashed ${BORDER}` }} />
            ))}
            {blocksByDay[d].map((b, i) => (
              <Tooltip key={i} title={`${b.it.playlist?.name} · ${scheduleSummary(b.it)}`}>
                <Box onClick={() => onEdit(b.it)}
                  sx={{
                    position: "absolute", left: 3, right: 3,
                    top: `${b.top}%`, height: `${Math.max(b.height, 4)}%`,
                    bgcolor: colorFor(b.it.playlist?.menu_type), opacity: b.allDay ? 0.35 : 0.9,
                    borderRadius: 1, cursor: "pointer", p: 0.5, overflow: "hidden",
                    color: "#fff", fontSize: 10, fontWeight: 700,
                    "&:hover": { opacity: 1, boxShadow: `0 0 0 2px ${GOLD}` },
                  }}>
                  {b.it.playlist?.name}
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

function scheduleSummary(it: IPlaylistGroupItem): string {
  const days = !it.days_of_week ? "Every day"
    : WEEK_DAYS.filter((_, i) => (it.days_of_week! & (1 << i)) !== 0).join(", ");
  const time = (!it.start_time && !it.end_time) ? "All day"
    : `${to12h(it.start_time)}–${to12h(it.end_time)}`;
  const dates = it.start_date || it.end_date
    ? ` · ${it.start_date ?? "…"}→${it.end_date ?? "…"}` : "";
  return `${days} · ${time}${dates}${it.priority ? ` · P${it.priority}` : ""}`;
}

export default ScheduleTab;
